import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const FRAMES = ["/parrot/wings-up.png", "/parrot/wings-mid.png", "/parrot/wings-down.png"];

function Parrot({ seed, flip, near }) {
  const flap = 0.95 + (seed % 3) * 0.12 + (near ? 0.1 : 0);
  return (
    <div
      className="relative"
      style={{
        width: near ? "clamp(180px, 20vw, 330px)" : "clamp(150px, 17vw, 290px)",
        transform: flip ? "scaleX(-1)" : undefined,
      }}
      data-testid={near ? "parrot-near" : "parrot"}
    >
      <div className="parrot-bob relative" style={{ animationDuration: `${flap}s` }}>
        <img src={FRAMES[1]} alt="" className="block w-full opacity-0" aria-hidden="true" />
        {FRAMES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            aria-hidden="true"
            className="parrot-frame absolute inset-0 block h-full w-full"
            style={{ animationName: `parrot-flap-${i}`, animationDuration: `${flap}s` }}
          />
        ))}
      </div>
    </div>
  );
}

function randomFlight(id) {
  const ltr = Math.random() < 0.5;
  const near = Math.random() < 0.25;
  const h = 5 + Math.random() * 50;
  const drift = (Math.random() - 0.5) * 18;
  return {
    id,
    group: Math.random() < 0.4 ? 2 : 1,
    ltr,
    near,
    startH: h,
    drift,
    duration: 10 + Math.random() * 5,
    tilt: (Math.random() - 0.5) * 8,
  };
}

function Flight({ flight, onDone }) {
  const from = flight.ltr ? "-14vw" : "114vw";
  const to = flight.ltr ? "114vw" : "-14vw";
  return (
    <motion.div
      className="absolute inset-0"
      initial={{ x: from, y: `${flight.startH}vh` }}
      animate={{
        x: to,
        y: [`${flight.startH}vh`, `${flight.startH + flight.drift}vh`, `${flight.startH + flight.drift * 0.35}vh`],
        rotate: [0, flight.tilt, 0],
      }}
      transition={{ duration: flight.duration, ease: "easeInOut", times: [0, 0.55, 1] }}
      onAnimationComplete={() => onDone(flight.id)}
      data-testid={`parrot-flight-${flight.id}`}
    >
      <div className="relative flex" style={{ gap: "2.5vw" }}>
        {Array.from({ length: flight.group }).map((_, i) => (
          <div key={i} style={{ marginTop: i * 40 }}>
            <Parrot seed={i + flight.id} flip={flight.ltr} near={flight.near && i === 0} />
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function BirdsLayer() {
  const [flights, setFlights] = useState([]);
  const idRef = useRef(0);
  const timerRef = useRef(null);
  const [reduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let alive = true;

    const spawn = () => {
      if (!alive) return;
      setFlights((current) => {
        const count = current.reduce((n, f) => n + f.group, 0);
        if (count >= 2) return current;
        const flight = randomFlight(++idRef.current);
        if (count + flight.group > 2) flight.group = 1;
        return [...current, flight];
      });
      timerRef.current = setTimeout(spawn, 4500 + Math.random() * 4500);
    };
    timerRef.current = setTimeout(spawn, 1500 + Math.random() * 2500);

    return () => {
      alive = false;
      clearTimeout(timerRef.current);
    };
  }, []);

  if (reduced) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[35] overflow-hidden"
      data-testid="birds-layer"
      aria-hidden="true"
    >
      {flights.map((f) => (
        <Flight key={f.id} flight={f} onDone={(id) => setFlights((cur) => cur.filter((x) => x.id !== id))} />
      ))}
    </div>
  );
}
